import { ImageServiceType } from '@omni/kit/Types';
import KitImage from '@omni/kit/components/KitImage';
import KitText from '@omni/kit/components/KitText';
import BorderRadius from '@omni/kit/theming/BorderRadius';
import Colors from '@omni/kit/theming/Colors';
import { parseImageUrl } from '@omni/kit/utilities/utilities';
import moment from 'moment';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { StyleSheet, View } from 'react-native';

const EVENT_IMAGE_SIZE_MEDIUM = 36;
const EVENT_IMAGE_SIZE_LARGE = 64;

export const EventImage = ({
  url,
  date,
  timezone,
  size = 'm',
  accessToken,
  customWidth,
  customHeight,
  customBorderRadius,
}: {
  url?: string;
  date?: string;
  timezone?: string;
  size?: 'm' | 'l';
  accessToken?: string | null | undefined;
  customWidth?: number | undefined;
  customHeight?: number | undefined;
  customBorderRadius?: number | undefined;
}): ReactElement => {
  const [finalImageUrl, setFinalImageUrl] = useState('');
  const width = customWidth
    ? customWidth
    : size === 'm'
    ? EVENT_IMAGE_SIZE_MEDIUM
    : EVENT_IMAGE_SIZE_LARGE;
  const height = customHeight
    ? customHeight
    : size === 'm'
    ? EVENT_IMAGE_SIZE_MEDIUM
    : EVENT_IMAGE_SIZE_LARGE;

  const headerFontStyles = useMemo(() => {
    switch (size) {
      case 'm':
        return { fontSize: 8, lineHeight: 10 };
      case 'l':
        return { fontSize: 12, lineHeight: 18 };
      default:
        break;
    }
  }, [size]);

  const bodyFontStyles = useMemo(() => {
    switch (size) {
      case 'm':
        return { fontSize: 14, lineHeight: 18 };
      case 'l':
        return { fontSize: 24, lineHeight: 26 };
      default:
        break;
    }
  }, [size]);

  // Fetch final image url to be passed to KitImage to be used as the cache key
  const getFinalImageUrl = useCallback(async () => {
    if (url && accessToken) {
      try {
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        setFinalImageUrl(response.url);
      } catch (e) {
        console.error('Failed to fetch image URL:', e);
      }
    }
  }, [url, accessToken]);

  useEffect(() => {
    getFinalImageUrl();
  }, [getFinalImageUrl]);

  let dateInTimezone = moment(date);

  if (timezone) {
    try {
      dateInTimezone = dateInTimezone.tz(timezone);
    } catch (_) {
      // pass
    }
  }

  return url ? (
    <View
      style={{
        width: width,
        height: height,
        backgroundColor: Colors.N100,
        borderRadius: customBorderRadius ? customBorderRadius : BorderRadius.m,
      }}
    >
      <KitImage
        style={{
          width: width,
          height: height,
          borderRadius: customBorderRadius
            ? customBorderRadius
            : BorderRadius.m,
        }}
        source={{
          uri: !accessToken
            ? url
            : parseImageUrl(
                finalImageUrl,
                width,
                height,
                ImageServiceType.ImagePng
              ),
        }}
      />
    </View>
  ) : (
    <View style={[styles.calendarContainer, { width: width, height: height }]}>
      <View style={styles.calendarHeader}>
        <KitText
          bold
          white
          style={{ ...headerFontStyles, textTransform: 'uppercase' }}
        >
          {dateInTimezone.format('MMM')}
        </KitText>
      </View>
      <View style={styles.calendarBody}>
        <KitText
          extraBold={size === 'l'}
          bold={size === 'm'}
          black
          style={bodyFontStyles}
        >
          {dateInTimezone.format('D')}
        </KitText>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  calendarContainer: {
    borderRadius: BorderRadius.m,
    borderWidth: 1,
    borderColor: Colors.N100,
    overflow: 'hidden',
  },
  calendarHeader: {
    alignItems: 'center',
    paddingVertical: 1,
    backgroundColor: Colors.N900,
  },
  calendarBody: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Colors.N0,
  },
});
